import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ui } from '@procuros/datachecks';

import { SsccLabelActions } from '../components/SsccLabelActions';
import { ssccLabelActionsWidth } from '../components/ssccLabelActionsWidth';

const SSCC_FIELD_PATH = 'unit_identifier';
const TRADE_UNIT_COUNT_FIELD_PATH = 'contained_trade_unit_count';

type TransportUnitFields = ui.TransportUnitsSection['fields'];
type FieldWithExtraFields = { extraFields?: Record<string, unknown> };

export const useDocumentTransportUnitsConfig = (config: TransportUnitFields, packingListEnabled = false) => {
  const configWithTradeUnitCount = useEnsureTradeUnitCountColumn(config, packingListEnabled);
  const configWithLockedFields = useLockPackingListFields(configWithTradeUnitCount, packingListEnabled);
  return useAddSsccLabelActions(configWithLockedFields, packingListEnabled);
};

// The enricher derives `contained_trade_unit_count` on save, so the column only displays it. A process
// spec that omits the field gets a trailing column anyway.
const useEnsureTradeUnitCountColumn = (config: TransportUnitFields, packingListEnabled: boolean) => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (!packingListEnabled || config.some((field) => field.path === TRADE_UNIT_COUNT_FIELD_PATH)) {
      return config;
    }

    const tradeUnitCountField: TransportUnitFields[number] = {
      path: TRADE_UNIT_COUNT_FIELD_PATH,
      type: ui.FieldTypes.number,
      modality: 'OPTIONAL' as const,
      label: t('webedi:transport_units.field.contained_trade_unit_count.label'),
      sort_order: Infinity,
      is_readonly: true,
      is_hidden: false,
      is_virtual: false,
      _internal: {
        level: 0,
        parent_transport_unit: '',
      },
    };

    return [...config, tradeUnitCountField];
  }, [config, packingListEnabled, t]);
};

// A plain `is_readonly` is not honored in edit mode, so these fields opt into the forced locks that
// createIsReadOnly applies in every mode and row. The SSCC keeps an escape hatch for a datacheck error,
// so a bad SSCC that arrives on an inbound document can still be fixed.
const useLockPackingListFields = (config: TransportUnitFields, packingListEnabled: boolean) => {
  return useMemo(() => {
    if (!packingListEnabled) {
      return config;
    }

    return config.map((field) => {
      const lockFlag = LOCK_FLAG_BY_PATH[field.path];
      return lockFlag
        ? {
            ...field,
            extraFields: { ...(field as FieldWithExtraFields).extraFields, [lockFlag]: true },
          }
        : field;
    });
  }, [config, packingListEnabled]);
};

const LOCK_FLAG_BY_PATH: Record<string, 'forceReadOnly' | 'forceReadOnlyUnlessError'> = {
  [TRADE_UNIT_COUNT_FIELD_PATH]: 'forceReadOnly',
  [SSCC_FIELD_PATH]: 'forceReadOnlyUnlessError',
};

const useAddSsccLabelActions = (config: TransportUnitFields, packingListEnabled: boolean) => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (!packingListEnabled) {
      return config;
    }

    return config.map((field) =>
      field.path === SSCC_FIELD_PATH
        ? {
            ...field,
            extraFields: {
              ...(field as FieldWithExtraFields).extraFields,
              // Only rendered in view mode (ViewWrapperCell is the sole consumer of meta.rightAddon).
              rightAddon: <SsccLabelActions />,
              rightAddonWidth: ssccLabelActionsWidth([
                t('webedi:transport_units.sscc_label.print_button.text'),
                t('webedi:transport_units.sscc_label.download_button.text'),
              ]),
            },
          }
        : field,
    );
  }, [config, packingListEnabled, t]);
};
